import { api } from './client';

export interface Strategy {
  id: number; slug: string; name: string; style: string;
  description: string; tags: string;
  annualReturn: number; maxDrawdown: number; holdingPeriodDays: number;
  turnover: number; userCount: number;
}

export interface Session {
  id: number; userId: number; strategyId: number; strategyName: string;
  status: string; fundAmount: number; totalAsset: number;
  floatingPnL: number; progressPct: number;
  lastCardText: string; lastCardAt: number;
  params: string; createdAt: number; updatedAt: number;
  cashBalance?: number;
}

export interface AlphaCard {
  id: number; sessionId: number;
  target: string; targetCode: string;
  action: string; amount: string;
  confidence: number; confidenceLabel: string;
  reason: string; sentiment: string; capitalFlow: string; techNote: string;
  sources: string; status: string; position: string;
  timelinePct: number; price: number; createdAt: number;
  roundId: number;
}

export interface AnalystView {
  id: number; sessionId: number; roundId: number;
  agentType: string; verdict: string; currentMessage: string;
  watchedTickers: string; metrics: string; createdAt: number;
}

export interface DebateRound {
  id: number; sessionId: number;
  ticker: string; tickerCode: string;
  bullPoints: string; bearPoints: string; riskAssessment: string;
  // Full bull/bear narrative paragraphs (Layer 3 看多/看空 researchers)
  // and the market context snapshot the LLM saw at decision time.
  // Populated for rounds created after the persistence fix; older rows
  // return empty strings.
  bullCase?: string; bearCase?: string; marketContext?: string;
  // JSON: { aggressive: "...", neutral: "...", conservative: "..." }
  riskStances?: string;
  finalAction: string; suggestedPosition: number;
  expectedPrice: string; stopLoss: string; takeProfit: string;
  confidence: number; bullConfidence: number; bearConfidence: number;
  reasoning: string; executed: boolean; createdAt: number;
}

// Per-rung exit stage in a position's TP / SL ladder.
export interface ExitStage {
  price: number;
  qtyPct: number;
  triggered: boolean;
  firedAt?: number;
}

export interface PositionExitPlan {
  originalQty: number;
  tpLadder?: ExitStage[];
  slLadder?: ExitStage[];
  trailingPct?: number;
  timeStop?: { maxDays: number; minProfitPct: number };
  sourceRoundId?: number;
}

export interface SessionHolding {
  ticker: string; tickerCode: string; industry: string;
  weight: number; roundCount: number;
  shares: number;
  avgCost: number;
  currentPrice: number;
  marketValue: number;
  pnl: number;
  pnlPct: number;
  // Laddered risk-exit configuration (TP/SL/trailing/time stop).
  // Null when the position predates the feature or seeding failed.
  exitPlan?: PositionExitPlan | null;
  openedAt?: number;    // unix s — most recent buy (resets on 加仓)
  firstBuyAt?: number;  // unix s — first ever buy (audit, "建仓日")
}

// Strategies
export const listStrategies = (style?: string) =>
  api.get<{ strategies: Strategy[] }>(`/alpha-lab/strategies${style ? `?style=${style}` : ''}`);

// qlib backtest comparison — Phase D. Headline summary used by the
// strategy market card; full result used by the report page.
export interface QlibBacktestSummary {
  totalReturn: number;
  sharpe: number;
  maxDrawdown: number;
  winRate: number;
  createdAt: number;
}

export interface QlibBacktestPoint { date: string; nav: number }

export interface QlibBacktestResult {
  points: QlibBacktestPoint[];
  total_return: number;
  annual_return: number;
  sharpe: number;
  max_drawdown: number;
  win_rate: number;
  turnover: number;
  benchmark_return: number;
  alpha: number;
  beta: number;
  information_ratio: number;
  benchmark_status?: string;
  calmar: number;
  sortino: number;
  cost_paid: number;
  cost_pct: number;
  monthly_returns?: Record<string, number>;
  trade_days: number;
  universe_size: number;
  strategy: string;
  rebalance_count: number;
  fills_attempted: number;
  fills_blocked_limit: number;
  fills_blocked_t1: number;
}

export interface QlibBacktestRun {
  id: number;
  strategyId: number;
  resultJson: string;       // JSON-encoded QlibBacktestResult
  totalReturn: number;
  sharpe: number;
  maxDrawdown: number;
  winRate: number;
  status: string;
  createdAt: number;
}

export const listStrategyBacktestSummaries = (ids: number[]) =>
  api.get<Record<string, QlibBacktestSummary>>(
    `/alpha-lab/strategies-backtest-summary?ids=${ids.join(',')}`,
  );

export const getStrategyQlibBacktest = (id: number) =>
  api.get<QlibBacktestRun | { stale: boolean; run: QlibBacktestRun } | { queued: boolean; message: string }>(
    `/alpha-lab/strategies/${id}/qlib-backtest`,
  );

// User-facing interactive backtest — returns full QlibBacktestResult synchronously.
export const runStrategyBacktest = (
  id: number,
  params?: { start?: string; end?: string; pools?: string; top_n?: number; rebalance?: string; fund?: number },
) => {
  const qs = new URLSearchParams();
  if (params?.start) qs.set('start', params.start);
  if (params?.end) qs.set('end', params.end);
  if (params?.pools) qs.set('pools', params.pools);
  if (params?.top_n) qs.set('top_n', String(params.top_n));
  if (params?.rebalance) qs.set('rebalance', params.rebalance);
  if (params?.fund) qs.set('fund', String(params.fund));
  const q = qs.toString();
  return api.get<QlibBacktestResult>(`/alpha-lab/strategies/${id}/backtest${q ? '?' + q : ''}`);
};

// Sessions
export const listSessions = () =>
  api.get<{ sessions: Session[]; total: number }>('/alpha-lab/sessions');

export const createSession = (body: {
  strategyId: number; fundAmount: number; params?: string;
}) => api.post<Session>('/alpha-lab/sessions', body);

export const getSession = (id: number) =>
  api.get<Session>(`/alpha-lab/sessions/${id}`);

// Update fund / preferences on an existing session. Backend merges over
// the existing params blob, so callers can omit fields they don't care
// about. Defaults to wiping derived stats (total_asset / floating_pnl)
// whenever fundAmount changes — opt out via resetStats: false.
export const updateSessionConfig = (id: number, body: {
  fundAmount?: number;
  sectors?: string[];
  risk?: 'conservative' | 'balanced' | 'aggressive';
  maxPosition?: number;
  resetStats?: boolean;
}) => api.patch<Session>(`/alpha-lab/sessions/${id}`, body);

export const pauseSession = (id: number) =>
  api.post<Session>(`/alpha-lab/sessions/${id}/pause`);

export const resumeSession = (id: number) =>
  api.post<Session>(`/alpha-lab/sessions/${id}/resume`);

export const endSession = (id: number) =>
  api.post<Session>(`/alpha-lab/sessions/${id}/end`);

// In-place strategy switch — updates strategy_id on the running session
// WITHOUT closing it, preserving debate rounds, cards, positions, and
// asset curve. Returns 404 when no running session exists.
export const switchSessionStrategy = (id: number, body: { strategyId: number }) =>
  api.patch<Session>(`/alpha-lab/sessions/${id}/switch-strategy`, body);

export interface StrategySwitchLog {
  id: number;
  sessionId: number;
  userId: number;
  fromStrategyId: number;
  fromStrategyName: string;
  toStrategyId: number;
  toStrategyName: string;
  switchedAt: number;
}

export const listStrategySwitchLogs = (sessionId: number) =>
  api.get<{ data: StrategySwitchLog[] }>(`/alpha-lab/sessions/${sessionId}/switch-logs`);

// Cards (analysis stream)
export const listCards = (sessionId: number, limit = 50) =>
  api.get<{ cards: AlphaCard[] }>(`/alpha-lab/sessions/${sessionId}/cards?limit=${limit}`);

// Analyst views — backend returns { analysts: [...] } (matches the URL).
//
// When a `round` is supplied the backend scopes views to that specific
// debate round; without it the endpoint falls back to the session-wide
// "latest per agent" set used by the live multi-agent panel. The
// per-round inspector MUST pass round, otherwise older rounds render
// as "无专家视图" while the freshest round's analysts get returned.
// Earlier the Type said `views` and silently shadowed the real field, so
// `d.views` was always undefined and the 4-维 analyst stream looked empty
// even when the worker had produced 20+ views.
export const listAnalystViews = (sessionId: number, roundId?: number) =>
  api.get<{ analysts: AnalystView[] }>(
    `/alpha-lab/sessions/${sessionId}/analysts${roundId ? `?round=${roundId}` : ''}`,
  );

// Debate rounds (trade decisions)
export const listDebateRounds = (sessionId: number) =>
  api.get<{ rounds: DebateRound[] }>(`/alpha-lab/sessions/${sessionId}/rounds`);

// Holdings
export const listHoldings = (sessionId: number) =>
  api.get<{ holdings: SessionHolding[] }>(`/alpha-lab/sessions/${sessionId}/holdings`);

// LLM trace audit — full prompt + CoT + raw response per call.
// Lightweight summary lands first; the user expands a row to fetch the
// heavy text columns via getLLMTrace. Keeps the list payload bounded
// even when a session has hundreds of trace rows.
export interface LLMTraceSummary {
  id: number;
  sessionId: number;
  roundId: number;
  stage: string;            // analyst.fundamental / bull / debate / risk.aggressive / card / ...
  model: string;
  durationMs: number;
  promptTokens: number;
  completionTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  hasCot: boolean;
  success: boolean;
  errorMsg: string;
  createdAt: number;
  // Phase: compact server-side summary of the JSON response (verdict /
  // finalAction / positionSuggestion etc.) — lets the trace list render
  // "this call decided X" on the collapsed row without a second fetch.
  responseSummary?: string;
}

export interface LLMTraceFull extends LLMTraceSummary {
  channelId: number;
  systemPrompt: string;
  userPrompt: string;
  rawResponse: string;
  cotTrace: string;
}

export const listLLMTraces = (
  sessionId: number,
  opts: { stage?: string; round?: number; since?: number; limit?: number; full?: boolean } = {},
) => {
  const params = new URLSearchParams();
  if (opts.stage) params.set('stage', opts.stage);
  if (opts.round) params.set('round', String(opts.round));
  if (opts.since) params.set('since', String(opts.since));
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.full) params.set('full', '1');
  const q = params.toString();
  // total is the un-capped count for the session (traces[] is limited);
  // the "已记录 N 次思考" badge reads total so it doesn't plateau at 500.
  return api.get<{ traces: LLMTraceSummary[]; total: number }>(
    `/alpha-lab/sessions/${sessionId}/traces${q ? '?' + q : ''}`,
  );
};

export const getLLMTrace = (traceId: number) =>
  api.get<LLMTraceFull>(`/alpha-lab/traces/${traceId}`);

// Persisted SSE event row used by the replay player. payload is a JSON
// string that decodes to the same shape as the live AlphaStreamEvent
// (use JSON.parse to deserialise).
export interface SessionEventRow {
  id: number;
  sessionId: number;
  roundSeq: number;
  eventType: string;
  stage: string;
  payload: string;
  createdAtNanos: number;
}

export const listSessionEvents = (
  sessionId: number,
  opts: { round?: number; since?: number; limit?: number } = {},
) => {
  const params = new URLSearchParams();
  if (opts.round) params.set('round', String(opts.round));
  if (opts.since) params.set('since', String(opts.since));
  if (opts.limit) params.set('limit', String(opts.limit));
  const q = params.toString();
  return api.get<{ events: SessionEventRow[] }>(
    `/alpha-lab/sessions/${sessionId}/events${q ? '?' + q : ''}`,
  );
};

// Per-stock AI snapshot — feeds the simtrade "AI 视角带". Each block is
// optional: when the worker hasn't produced anything yet for this ticker
// the strip shows a friendly placeholder instead.
export interface StockSnapshotCard {
  id: number; target: string; targetCode: string;
  action: string; position: string;
  confidence: number; confidenceLabel: string;
  reason: string; sentiment: string;
  capitalFlow: string; techNote: string;
  createdAt: number;
}
export interface StockSnapshotRound {
  id: number; ticker: string; tickerCode: string;
  finalAction: string; suggestedPosition: number;
  expectedPrice: string; stopLoss: string; takeProfit: string;
  confidence: number; reasoning: string; riskAssessment: string;
  createdAt: number;
}
export interface StockSnapshotAnalyst {
  id: number; agentType: string;
  verdict: string; currentMessage: string;
  rating: string;       // empty when this analyst didn't single out the code
  createdAt: number;
}
export interface StockSnapshot {
  sessionId: number;
  stockCode: string;
  latestCard: StockSnapshotCard | null;
  latestRound: StockSnapshotRound | null;
  analysts: StockSnapshotAnalyst[];
  generatedAt: number;
}

export const getStockSnapshot = (sessionId: number, code: string) =>
  api.get<StockSnapshot>(`/alpha-lab/sessions/${sessionId}/stocks/${encodeURIComponent(code)}/snapshot`);

// Asset curve & report
export interface CurvePoint { date: string; totalAsset: number; dailyReturn: number; }

export interface CurveStats {
  totalReturn: number; maxDrawdown: number; sharpe: number;
  winDays: number; lossDays: number;
}

export interface ReportOverview {
  totalReturn: number;
  // totalAsset / floatingPnL are recomputed live from sim_positions +
  // latest quotes so they don't lag the user's actual book between
  // worker decision ticks. alpha_sessions.total_asset is only updated
  // when the worker fires a new tick — between ticks it can disagree
  // with the live PnL by several thousand.
  totalAsset: number;
  // floatingPnL = unrealized + realized = totalAsset − fund.
  // Kept for back-compat; new UI should use the split below.
  floatingPnL: number;
  unrealizedPnL?: number; // 浮动 (持仓 mark-to-market)
  realizedPnL?: number;   // 落袋 (已平仓累计, 不含手续费)
  feeTotal?: number;      // 手续费 (commission + tax, 累计支出, >= 0)
  totalTrades: number;
  // winRate: -1 sentinel = "no closed trades yet" (still all-open
  // positions). Frontend should render "暂无平仓" rather than 0% / 100%.
  winTrades: number; closedTrades: number; winRate: number;
  maxDrawdown: number;
  // sharpeSampleN: number of non-zero daily returns that fed the
  // sharpe calc. <= 2 means "样本不足" — UI should suppress the value.
  sharpe: number; sharpeSampleN: number;
  avgConfidence: number;
}

export interface ReportByStock {
  ticker: string; tickerCode: string; name: string; industry: string;
  trades: number; buyAmt: number; sellAmt: number; pnlAmt: number;
  pnlPct: number; contribution: number;
  // P2-7: distinguishes 持仓中 (currentQty > 0) from 已平仓 in the
  // 个股贡献度 list so the user knows if the PnL is locked or floating.
  isHolding?: boolean;
}

export interface ReportSectorDistribution {
  industry: string; weight: number; count: number;
}

export interface ReportKeyDecision {
  ticker: string; tickerCode?: string; action: string; confidence: number;
  summary: string; createdAt: number;
}

export interface MonthlyWinRate {
  month: string;        // "4月"
  winRate: number;      // overall % of rounds whose CreatedAt day had positive curve return
  buyWin: number;       // same metric restricted to buy/加仓 rounds
  sellWin: number;      // same metric restricted to sell/退出 rounds
  totalTrades: number;
  profit: number;       // month-over-month total asset change %
}

export interface SessionReport {
  overview: ReportOverview;
  byStock: ReportByStock[];
  sectorDistribution: ReportSectorDistribution[];
  keyDecisions: ReportKeyDecision[];
  curve: CurvePoint[];
  monthlyWinRate: MonthlyWinRate[];
}

export const getAssetCurve = (sessionId: number, days = 30) =>
  api.get<{ curve: CurvePoint[]; stats: CurveStats }>(`/alpha-lab/sessions/${sessionId}/asset-curve?days=${days}`);

// Time-slot broadcasts (platform-level, generated by the in-server scheduler).
export type BroadcastSlotType = 'open' | 'morning' | 'afternoon' | 'close' | 'weekend';

export interface MarketBroadcast {
  id: number;
  slotAt: number;        // unix seconds
  slotType: BroadcastSlotType;
  title: string;
  content: string;
  metrics: string;       // JSON string; parse on demand
  createdAt: number;
}

export const listRecentBroadcasts = (days = 7, type?: BroadcastSlotType) => {
  const qs = `?days=${days}` + (type ? `&type=${type}` : '');
  return api.get<{ broadcasts: MarketBroadcast[] }>(`/alpha-lab/broadcasts/recent${qs}`);
};

export const getSessionReport = (sessionId: number) =>
  api.get<SessionReport>(`/alpha-lab/sessions/${sessionId}/report`);

export interface AlphaStats {
  runningSessions: number;
  totalSessions: number;
  todayCards: number;
  todayTrades: number;          // legacy: AI broadcast buy-action count
  todayFilledOrders: number;    // real sim_orders filled today
  todayBuyOrders: number;       // sim_orders filled buy today
  todaySellOrders: number;      // sim_orders filled sell today
  isTradingHoursNow: boolean;   // A-share session currently open
  totalAsset: number;
  totalPnL: number;
}

export const getAlphaStats = () =>
  api.get<AlphaStats>('/alpha-lab/stats');

export const checkAlphaAccess = () =>
  api.get<{ subscribed: boolean; productSlug: string }>('/alpha-lab/access');
